import React, { useState, useEffect } from "react";
import { Box, Typography, Paper } from "@mui/material";
import languageLearningApi from "../language_learning/middleware/languageLearningApi";

const LearningPathBreadcrumb = ({ metadata, courseTitle }) => {
  const [courseletData, setCourseletData] = useState(null);
  const [sectionData, setSectionData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Cache for courselet and section data to avoid repeated API calls
  const [courseletCache, setCourseletCache] = useState({});
  const [sectionCache, setSectionCache] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      if (!metadata || !metadata.courseletId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch courselet data (from cache or API)
        let currentCourseletData;
        if (courseletCache[metadata.courseletId]) {
          currentCourseletData = courseletCache[metadata.courseletId];
        } else {
          const courseletResponse = await languageLearningApi.get(
            `/courselets/${metadata.courseletId}/details`
          );
          currentCourseletData = courseletResponse.data.courselet;
          
          // Update cache
          setCourseletCache(prev => ({
            ...prev,
            [metadata.courseletId]: currentCourseletData
          }));
        }
        setCourseletData(currentCourseletData);
        
        // Fetch section data if we have a sectionId
        if (currentCourseletData.sectionId) {
          let currentSectionData;
          if (sectionCache[currentCourseletData.sectionId]) {
            currentSectionData = sectionCache[currentCourseletData.sectionId];
          } else {
            const sectionResponse = await languageLearningApi.get(
              `/sections/${currentCourseletData.sectionId}`
            );
            currentSectionData = sectionResponse.data.section;
            
            // Update cache
            setSectionCache(prev => ({
              ...prev,
              [currentCourseletData.sectionId]: currentSectionData
            }));
          }
          setSectionData(currentSectionData);
        }
      } catch (error) {
        console.error("Error fetching learning path data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [metadata?.courseletId]);

  if (loading || !metadata?.courseletId) {
    return null; // Don't render anything while loading or if no courselet data
  }

  return (
    <Paper 
      elevation={1}
      sx={{
        padding: 1.5,
        // mb: 2,
        borderRadius: 2,
        background: 'rgb(232, 245, 248)',
        borderLeft: '4px solid rgb(75, 66, 152)',
        
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography 
          variant="subtitle2" 
          color="text.secondary"
          sx={{ 
            fontSize: '0.7rem', 
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            mb: 0.5
          }}
        >
          Now Learning
        </Typography>
        
        <Typography 
          variant="h6"
          sx={{ 
            fontSize: '1rem', 
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}
        >
          {courseTitle || 'Course'}
          {sectionData && (
            <>
              <Box 
                component="span" 
                sx={{ 
                  mx: 0.7,
                  color: 'text.secondary',
                  fontSize: '0.9rem'
                }}
              >
                &gt;
              </Box>
              {sectionData.title}
            </>
          )}
          {courseletData && (
            <>
              <Box 
                component="span" 
                sx={{ 
                  mx: 0.7,
                  color: 'text.secondary',
                  fontSize: '0.9rem'
                }}
              >
                &gt;
              </Box>
              <Typography 
                component="span"
                sx={{ 
                  color: 'rgb(75, 61, 152)',
                  fontWeight: 700,
                }}
              >
                {courseletData.title}
              </Typography>
            </>
          )}
        </Typography>
      </Box>
    </Paper>
  );
};

export default LearningPathBreadcrumb;